'use client';

import { Button, Separator, cn } from '@baseball-near-you/ui';
import { ClaimCard } from '@bb/components/Settings/Team/ClaimTeam/ClaimCard';
import { NoTeamsMessage } from '@bb/components/Settings/Team/ClaimTeam/NoTeamsMessage';
import { QueryKey } from '@bb/lib/hooks/QueryKey';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { TeamSelectionModal } from '../../Settings/Team/ClaimTeam/TeamSelectionModal';
import { OnboardingStep } from './types';

export const TeamClaimStep = (props: {
  onStepSuccess: () => void;
  setCurrentStep: (step: OnboardingStep) => void;
}) => {
  const queryClient = useQueryClient();
  const { userProfile } = useUserProfile();

  useEffect(() => {
    posthog.capture('claim_team_step_viewed', {
      user_id: userProfile?.id,
    });
  }, [userProfile?.id, posthog]);
  return (
    <div className="flex w-full flex-col pb-8">
      <p className="mb-5 text-gray-800 sm:mt-10">
        You may claim this profile if you are a coach or owner of this
        organization
      </p>
      <TeamSelectionModal
        hideCantFindTeam
        onSuccess={async () => {
          await queryClient.invalidateQueries({
            queryKey: [QueryKey.User],
          });
        }}
        triggerNode={({ open, setOpen }) => (
          <Button
            className="w-full bg-red hover:bg-red/90"
            onClick={() => setOpen(!open)}
          >
            Select a team to claim
          </Button>
        )}
        footerNode={
          <div className="mt-2 hidden w-full flex-col items-center rounded-sm border-2 border-gray-500 border-dashed bg-gray-50 py-6 sm:flex">
            <p className="font-medium text-base text-gray-600">
              Can't find your team in our system?
            </p>
            <Button
              className="mt-2 bg-red hover:bg-red/90"
              onClick={() => {
                props.setCurrentStep(OnboardingStep.TeamAdd);
              }}
            >
              add a new team organization
            </Button>
          </div>
        }
      />

      <div className="mt-4 flex flex-col items-center sm:mt-6">
        <Button
          className={cn(
            'mx-2 mt-3 w-full',
            (userProfile?.claims ?? []).length > 0 ? 'bg-red' : ''
          )}
          variant={
            (userProfile?.claims ?? []).length > 0 ? 'default' : 'outline'
          }
          onClick={() => {
            props.onStepSuccess();
          }}
        >
          {(userProfile?.claims ?? []).length > 0 ? 'Continue' : 'Skip for now'}
        </Button>
      </div>

      <NoTeamsMessage
        onAddTeam={() => props.setCurrentStep(OnboardingStep.TeamAdd)}
      />

      {(userProfile?.claims ?? []).length > 0 && (
        <>
          <Separator className="mt-12" />
          <div className="mt-8 flex flex-col">
            <p className="font-semibold text-lg">Pending claims:</p>
            <p className="text-gray-700 text-sm">
              We will review your claim and get back to you soon.
            </p>
            <div className="mt-2 flex flex-col gap-2">
              {userProfile?.claims.map((claim) => (
                <ClaimCard claim={claim} key={claim.id} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
